import { DateRangePicker } from 'components/DateRangePicker/DateRangePicker'
import { MANTINE_DATE_RANGE_PICKER_WIDTH } from 'components/DateRangePicker/MantineDateRangePickerWithFooter/MantineDateRangePickerWithFooter'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'

// Height of the date-range picker input. Exported so WidgetCard can floor its header height to fit
// this control (keeping title-only headers the same height as the chart header that carries it).
export const DASHBOARD_DATE_PICKER_HEIGHT = 36

export const DashboardDateRangePicker = ({
  dateFrom,
  dateTo,
  onChange,
  // Opt-in per dashboard. Historical-only (e.g. Water Usage) disables future dates + the future-oriented
  // presets. Forecast dashboards (e.g. the pest chart) leave this false to keep future dates/presets.
  isHistoricalOnly = false,
}: {
  dateFrom: moment.Moment
  dateTo: moment.Moment
  onChange: (from: moment.Moment, to: moment.Moment) => void
  isHistoricalOnly?: boolean
}) => {
  const timezone = moment.tz.guess()

  return (
    // The shared DateRangePicker hard-codes a dark (grey800) input border; soften it to grey200 so it
    // matches the property Select, whose border comes from the global theme's default Input style
    // (theme.colors.grey[1] === colors.grey200). Scoped to this wrapper so the shared component is
    // left untouched. !important because the picker sets that border as an inline style.
    <div css={{ '& [class*="Input-input"]': { borderColor: `${colors.grey200} !important` } }}>
      <DateRangePicker
        dateFrom={dateFrom}
        dateTo={dateTo}
        height={DASHBOARD_DATE_PICKER_HEIGHT}
        // applied inline, so it must match the slot the picker's css class reserves
        width={MANTINE_DATE_RANGE_PICKER_WIDTH}
        timezone={timezone}
        // Historical-only clamps maxDate to today + drops the future presets (the shared picker otherwise
        // defaults maxDate to today + 14 days). Applies to Water Usage + the pest chart.
        isHistoricalOnly={isHistoricalOnly}
        handleApply={({ value, setDropdownOpened }) => {
          if (!value || !value[0] || !value[1]) return

          // A calendar selection hands back midnight of each chosen date, so without this the window
          // ends at 00:00 on the end date and drops that whole day. That went unnoticed while every
          // figure came from daily rows stamped at local midnight, which the inclusive end bound still
          // admits, but anything read from hourly or raw rows (On Duration) loses the final day.
          // Widening to whole local days is what a date-only picker implies, and it matches the
          // default range in useWaterDashboardPreferences. The presets already pass whole days, so
          // this is a no-op for them.
          onChange(moment.tz(value[0], timezone).startOf('day'), moment.tz(value[1], timezone).endOf('day'))

          setDropdownOpened(false)
        }}
      />
    </div>
  )
}
